import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { Subscription } from 'rxjs';
import { Orcamento } from '../../core/models/workflow.models';
import { OrcamentosService } from '../../core/services/orcamentos.service';

interface ConsultorCard {
  nome: string;
  qtdAbertos: number;
  qtdFechados: number;
  valorAbertos: number;
  valorFechados: number;
  total: number;
  donutData: ChartData<'doughnut'>;
}

const GARGALOS_MES = [
  { etapa: 'Programacao Reparo', qtd: 5 },
  { etapa: 'Analise de Complemento', qtd: 2 },
  { etapa: 'Funilaria', qtd: 3 },
];

@Component({
  selector: 'app-dashboards',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective],
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
})
export class DashboardsComponent implements OnInit, OnDestroy {
  dataDe = '';
  dataAte = '';

  consultores = ['Carla Mendes', 'Marcos Silva'];
  consultorCards: ConsultorCard[] = [];
  geralCard: ConsultorCard | null = null;

  barData: ChartData<'bar'> = {
    labels: [],
    datasets: [],
  };

  donutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed ?? 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  barOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end',
        align: 'end',
        formatter: (value: number) => value,
        font: { size: 14, weight: 700 },
        color: '#111827',
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 10,
          font: { size: 14, weight: 600 },
          color: '#111827',
        },
      },
      y: {
        ticks: {
          font: { size: 13 },
          color: '#6b7280',
          precision: 0,
        },
      },
    },
  };

  private subscription = new Subscription();
  private leads: Orcamento[] = [];

  constructor(private orcamentosService: OrcamentosService) {}

  ngOnInit(): void {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dataDe = this.formatDateInput(firstDay);
    this.dataAte = this.formatDateInput(now);

    this.subscription.add(
      this.orcamentosService.getAll().subscribe((orcamentos) => {
        this.leads = orcamentos.filter((item) => item.status === 'LEAD');
        this.rebuildDashboards();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  aplicarFiltro(): void {
    this.rebuildDashboards();
  }

  private rebuildDashboards(): void {
    const de = this.parseDate(this.dataDe);
    const ate = this.parseDate(this.dataAte, true);
    this.consultorCards = this.consultores.map((nome) => this.buildConsultorCard(nome, de, ate));
    this.geralCard = this.buildConsultorCard('Geral', de, ate, true);
    this.barData = this.buildGargalosMock();
  }

  private buildConsultorCard(
    nome: string,
    de: Date,
    ate: Date,
    geral = false
  ): ConsultorCard {
    const leadsFiltrados = this.leads.filter((lead) => {
      const matchConsultor = geral
        ? true
        : (lead.consultor ?? lead.consultorResponsavel) === nome;
      return (
        matchConsultor &&
        this.inRange(this.parseDateValue(lead.dataCriacao ?? lead.criadoEm), de, ate)
      );
    });

    const abertos = leadsFiltrados.filter((lead) => lead.statusLead === 'ABERTO');
    const fechados = leadsFiltrados.filter(
      (lead) => lead.statusLead === 'FECHADO' && lead.virouOs
    );

    const qtdAbertos = abertos.length;
    const qtdFechados = fechados.length;
    const valorAbertos = abertos.reduce(
      (sum, lead) => sum + (lead.valorOrcamento ?? lead.valor ?? 0),
      0
    );
    const valorFechados = fechados.reduce(
      (sum, lead) => sum + (lead.valorOrcamento ?? lead.valor ?? 0),
      0
    );
    const total = leadsFiltrados.reduce(
      (sum, lead) => sum + (lead.valorOrcamento ?? lead.valor ?? 0),
      0
    );

    return {
      nome,
      qtdAbertos,
      qtdFechados,
      valorAbertos,
      valorFechados,
      total,
      donutData: {
        labels: [`Em aberto (${qtdAbertos})`, `Fechados (OS) (${qtdFechados})`],
        datasets: [
          {
            data: [qtdAbertos, qtdFechados],
            backgroundColor: ['rgba(35, 28, 70, 0.6)', 'rgba(26, 160, 83, 0.6)'],
            borderColor: ['#231c46', '#1aa053'],
          },
        ],
      },
    };
  }

  private buildGargalosMock(): ChartData<'bar'> {
    return {
      labels: GARGALOS_MES.map((item) => item.etapa),
      datasets: [
        {
          data: GARGALOS_MES.map((item) => item.qtd),
          backgroundColor: 'rgba(219, 53, 69, 0.5)',
          borderColor: '#db3545',
        },
      ],
    };
  }

  private parseDate(value: string, endOfDay = false): Date {
    const date = new Date(value);
    if (endOfDay) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  }

  private parseDateValue(value?: Date | string | null): Date {
    if (!value) {
      return new Date(0);
    }
    return value instanceof Date ? value : new Date(value);
  }

  private inRange(date: Date, de: Date, ate: Date): boolean {
    return date >= de && date <= ate;
  }

  private formatDateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}
